import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import * as React from 'react';

import { CondensedTip, useCondensed } from '@/design';
import {
  emptyRoot,
  nameContainsOf,
  rootChips,
  withNameContains,
  withoutNodes,
  type ICriteriaGroup,
  type ICriteriaWords,
  type IFilterChip
} from '../../list-criteria';

/**
 * The filter in words (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.2 and §3.4): a chip per root condition — "State is NY or CT", "Created since the cycle
 * opened" — each with its ✕; a nested group as one chip, "3 conditions, any of them", whose press
 * opens *Advanced…* on it; the name box's condition a chip of its own. Every chip is one of the
 * tree's doors (`rootChips` in `list-criteria.ts` says them all); none spells a code or an
 * operator name (§0.1). A chip's press is the caller's to answer (`onChipPress`, by the chip's
 * `door`) — Lists opens the column's funnel, the name box, or *Advanced…* on the node; with no
 * caller's answer, and for a nested group's chip always, it is *Advanced…* on the chip's node.
 *
 * Changing the filter is the screen's state, never saved here: the caller marks the view changed
 * and reads the rows again.
 *
 * In a work page's compact bar (`condense.tsx`) the chips keep to one line: those past its end are
 * gathered behind "+N", whose popover holds them, each still pressable and removable.
 */
export function FilterChips({
  root,
  words,
  onChange,
  onChipPress,
  onAdvanced,
  problemsOf,
  advancedLink = true,
  lead,
  hidden = [],
  children
}: {
  root: ICriteriaGroup;
  words: ICriteriaWords;
  onChange: (root: ICriteriaGroup) => void;
  /** A chip pressed, and its press — where it leads is the caller's (the funnel takes the focus
   *  back to `from` as it closes). */
  onChipPress?: (chip: IFilterChip, from: HTMLElement) => void;
  /** *Advanced…*, opened on a node (a nested group's chip) or on the whole tree. */
  onAdvanced: (nodeId?: string) => void;
  /** survey-api's refusals placed on a node (`placeProblems`) — said on the chip that holds it. */
  problemsOf?: (nodeId: string) => readonly string[];
  /** Draws *Advanced…* after the chips — a work page draws it in its toolbar instead. */
  advancedLink?: boolean;
  /** Drawn before the chips — a work page's card signals on. */
  lead?: React.ReactNode;
  /** Nodes the caller says itself (`lead`), drawn as no chip of their own. */
  hidden?: readonly string[];
  /** Drawn after the chips — a work page's *Clear all*. */
  children?: React.ReactNode;
}) {
  const chips = rootChips(root, words).filter((chip) => !chip.nodeIds.every((id) => hidden.includes(id)));
  const hasProblems = (chip: IFilterChip): boolean =>
    chip.nodeIds.some((id) => (problemsOf?.(id) ?? []).length > 0);
  const line = React.useRef<HTMLDivElement>(null);
  const { condensed } = useCondensed();
  const fit = useOneLine(line, chips);
  const drawChip = (chip: IFilterChip, hidden = false) => (
    <Chip
      key={chip.key}
      chip={chip}
      hidden={hidden}
      invalid={hasProblems(chip)}
      onPress={(from) =>
        chip.door === 'advanced' || !onChipPress
          ? onAdvanced(chip.nodeIds[0])
          : onChipPress(chip, from)
      }
      onRemove={() => onChange(withoutNodes(root, chip.nodeIds))}
    />
  );
  const rest = chips.slice(fit);
  return (
    <div
      ref={line}
      className={cn('flex min-w-0 items-center gap-1.5', condensed ? 'flex-nowrap' : 'flex-wrap')}
      role="group"
      aria-label="Filters"
    >
      {lead}
      {chips.map((chip, index) => drawChip(chip, index >= fit))}
      {rest.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-chips-more=""
              className="h-7 rounded-full px-2.5 text-[0.8125rem]"
              aria-label={`${rest.length} more ${rest.length === 1 ? 'filter' : 'filters'}`}
            >
              +{rest.length}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" aria-label="More filters" className="flex w-auto max-w-[min(36rem,90vw)] flex-wrap gap-1.5 p-2">
            {rest.map((chip) => drawChip(chip))}
          </PopoverContent>
        </Popover>
      )}
      {advancedLink && <AdvancedLink onPress={() => onAdvanced()} />}
      {children}
      {chips.some(hasProblems) && (
        <ul role="alert" className="basis-full text-[0.8125rem] text-destructive">
          {chips.flatMap((chip) =>
            chip.nodeIds.flatMap((id) =>
              (problemsOf?.(id) ?? []).map((reason, index) => (
                <li key={`${id}-${index}`}>
                  {chip.words}: {reason}
                </li>
              ))
            )
          )}
        </ul>
      )}
    </div>
  );
}

/** *Advanced…*, the whole filter tree's builder — its icon alone, of the same name, in a compact bar. */
export function AdvancedLink({ onPress }: { onPress: () => void }) {
  const { condensed } = useCondensed();
  return (
    <CondensedTip label="Advanced…">
      <Button
        type="button"
        variant={condensed ? 'outline' : 'link'}
        className={condensed ? 'size-8 p-0' : 'h-auto gap-1 p-0 text-[0.8125rem]'}
        aria-label={condensed ? 'Advanced…' : undefined}
        onClick={onPress}
      >
        <SlidersHorizontal className={condensed ? 'size-4' : 'size-3.5'} aria-hidden />
        {!condensed && 'Advanced…'}
      </Button>
    </CondensedTip>
  );
}

/** Room for "+N" at a condensed line's end. */
const MORE_WIDTH = 48;
/** The line's `gap-1.5`. */
const CHIP_GAP = 6;

/**
 * How many of `chips` fit a condensed line beside what follows them — every one, uncondensed. Each
 * chip's width is kept as last drawn, so one gathered behind "+N" is still counted.
 */
function useOneLine(line: React.RefObject<HTMLDivElement>, chips: readonly IFilterChip[]): number {
  const { condensed } = useCondensed();
  const widths = React.useRef(new Map<string, number>());
  const [fit, setFit] = React.useState(Infinity);
  React.useLayoutEffect(() => {
    const box = line.current;
    if (!condensed || !box) {
      setFit(Infinity);
      return;
    }
    const measure = () => {
      let tail = 0;
      for (const element of box.children) {
        if (!(element instanceof HTMLElement) || element.hidden || element.hasAttribute('data-chips-more')) continue;
        const key = element.dataset.chipKey;
        if (key !== undefined) widths.current.set(key, element.offsetWidth);
        else if (element.tagName !== 'UL') tail += element.offsetWidth + CHIP_GAP;
      }
      const width = (chip: IFilterChip) => (widths.current.get(chip.key) ?? 0) + CHIP_GAP;
      const all = chips.reduce((sum, chip) => sum + width(chip), 0);
      let room = box.clientWidth - tail;
      if (all <= room) {
        setFit(Infinity);
        return;
      }
      room -= MORE_WIDTH;
      let count = 0;
      while (count < chips.length && room >= width(chips[count])) room -= width(chips[count++]);
      setFit(count);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [line, condensed, chips.map((chip) => chip.key).join('\n')]);
  return fit;
}

/**
 * One chip, its words cut with an ellipsis past `32rem` (or the row, on a narrow screen): a
 * migrated view's "Firm Name is not …" names dozens of firms, and drawn whole one chip was wider
 * than the screen. The words stay whole where they are read — the press's accessible name, the ✕'s,
 * the problems' list — and a clipped chip says them in a tooltip on hover or focus, a chip that fits
 * none; it is the kit's tooltip, never a `title`, which a keyboard never opens. A work page draws
 * it on its applied filters' row (`workFilterChips`).
 */
export function Chip({
  chip,
  hidden = false,
  invalid,
  onPress,
  onRemove
}: {
  chip: IFilterChip;
  /** Past a condensed line's end, said behind "+N" instead. */
  hidden?: boolean;
  invalid: boolean;
  onPress: (from: HTMLElement) => void;
  onRemove: () => void;
}) {
  const press = React.useRef<HTMLButtonElement>(null);
  const [whole, setWhole] = React.useState(false);
  // Measured as the tooltip asks to open, not observed: the row reflows with every chip and column.
  // A pixel's slack, the kit's `useOverflow`'s: a fitting chip's widths can round apart.
  function clipped(): boolean {
    const element = press.current;
    return element !== null && element.scrollWidth > element.clientWidth + 1;
  }
  return (
    <span
      hidden={hidden}
      data-chip={chip.field ?? ''}
      data-chip-key={chip.key}
      data-invalid={invalid ? '' : undefined}
      className={
        'inline-flex max-w-[min(100%,32rem)] items-center rounded-full border bg-[var(--card)] text-[0.8125rem] ' +
        (invalid ? 'border-destructive' : 'border-border')
      }
    >
      <Tooltip open={whole} onOpenChange={(open) => setWhole(open && clipped())}>
        <TooltipTrigger asChild>
          {/* No description: the tooltip repeats the name, and a reader would hear the words twice. */}
          <button
            ref={press}
            type="button"
            aria-describedby={undefined}
            onClick={(event) => onPress(event.currentTarget)}
            className="min-w-0 truncate rounded-l-full py-0.5 pl-2.5 pr-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {chip.words}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-h-[60dvh] max-w-lg overflow-y-auto break-words">{chip.words}</TooltipContent>
      </Tooltip>
      <button
        type="button"
        aria-label={`Remove the filter: ${chip.words}`}
        onClick={onRemove}
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </span>
  );
}

/**
 * `Filters (n) ✕` — how many chips the filter holds, and the one press that clears them all. Drawn
 * only while there is something to clear; a filter of nothing is every row.
 */
export function FiltersClear({
  root,
  words,
  onChange
}: {
  root: ICriteriaGroup;
  words: ICriteriaWords;
  onChange: (root: ICriteriaGroup) => void;
}) {
  const count = rootChips(root, words).length;
  if (count === 0) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      className="h-8 gap-1 px-2 font-normal"
      aria-label={`Clear every filter (${count})`}
      onClick={() => onChange(emptyRoot())}
    >
      Filters ({count})
      <X className="size-3.5" aria-hidden />
    </Button>
  );
}

/**
 * *Name contains…* — a box over `sys.display_name contains`, submitted on Enter, cleared with its
 * chip's ✕ (§3.4). It is a root condition of its own, beside Name's funnel, and the chips say it
 * like any other; the box shows what the tree holds, so a ✕ on its chip empties it.
 */
export function NameContainsBox({
  root,
  onChange,
  inputRef
}: {
  root: ICriteriaGroup;
  onChange: (root: ICriteriaGroup) => void;
  /** The box, which its chip's press focuses. */
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const held = nameContainsOf(root);
  const [text, setText] = React.useState(held);
  React.useEffect(() => setText(held), [held]);
  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const next = text.trim();
        if (next !== held) onChange(withNameContains(root, next));
      }}
      className="relative inline-flex items-center"
    >
      <Search className="pointer-events-none absolute left-2 size-3.5 text-muted-foreground" aria-hidden />
      <Input
        ref={inputRef}
        type="search"
        aria-label="Name contains"
        placeholder="Name contains…"
        value={text}
        onChange={(event) => setText(event.target.value)}
        className="h-8 w-52 pl-7"
      />
    </form>
  );
}
